import { useQuery } from '@tanstack/react-query';
import { ClientError } from 'graphql-request';
import { fetchGql } from '@/lib/graphql_client';
import { authFetch } from '@/lib/rest_client';
import { MeDocument } from '@/gql/graphql';

const isGqlValidationError = (err: unknown): boolean =>
  err instanceof ClientError &&
  (err.response?.errors?.some((e) => e.extensions?.code === 'GRAPHQL_VALIDATION_FAILED') ?? false);

/**
 * The caller's platform-user id (`adminPlatformUserId` for onboarding flows).
 *
 * Primary: `me { platformUserId }` (ENT-88 — user-level id, stable across all
 * of the caller's memberships). Falls back to the REST deprecation shim
 * `GET /auth/contexts` (reading `platformUserId` off the first row) when the
 * GraphQL field is not yet available on the backend, matching the same
 * fallback pattern as {@link ../session/use-my-contexts}.
 */
export function usePlatformUserId(): string | undefined {
  const { data } = useQuery({
    queryKey: ['auth', 'platform-user-id'],
    queryFn: async (): Promise<string | undefined> => {
      try {
        const { me } = await fetchGql(MeDocument);
        return me.platformUserId;
      } catch (err) {
        if (!isGqlValidationError(err)) throw err;
        const rows = await authFetch<Array<{ platformUserId: string }>>('/auth/contexts');
        return rows[0]?.platformUserId;
      }
    },
    staleTime: 5 * 60_000,
  });
  return data;
}
